import React, { useState } from "react";
import { X, Calendar, Save, X as XIcon, Trash2 } from "lucide-react";
import {
	TextField,
	IconButton,
	Tooltip,
	CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from "date-fns";
import { apiClient } from "../../services/api";

const MilestoneDetailModal = ({
	isOpen,
	onClose,
	rowData,
	onMilestoneUpdate,
}) => {
	const [editingVDAT, setEditingVDAT] = useState({});
	const [vdatValues, setVdatValues] = useState({});
	const [saving, setSaving] = useState({});
	const [clearing, setClearing] = useState({});

	// Initialize VDAT values when modal opens
	React.useEffect(() => {
		if (isOpen && rowData) {
			const initialValues = {};
			milestones.forEach((milestone) => {
				if (milestone.hasDetails) {
					const detailData = rowData.milestones_detail?.[milestone.key];
					initialValues[milestone.key] = detailData?.vdat || "";
				}
			});
			setVdatValues(initialValues);
			setEditingVDAT({});
			setSaving({});
			setClearing({});
		}
	}, [isOpen, rowData]);

	if (!isOpen || !rowData) return null;

	// Helper function to convert DD/MM/YYYY to Date object
	const parseDate = (dateStr) => {
		if (!dateStr || dateStr === "-") return null;
		try {
			// Handle DD/MM/YYYY format
			if (dateStr.includes("/")) {
				return parse(dateStr, "dd/MM/yyyy", new Date());
			}
			return null;
		} catch (e) {
			return null;
		}
	};

	// Helper function to convert Date object to DD/MM/YYYY
	const formatDate = (date) => {
		if (!date) return "";
		try {
			return format(date, "dd/MM/yyyy");
		} catch (e) {
			return "";
		}
	};
	// Save VDAT value
	const handleSaveVDAT = async (milestoneKey) => {
		setSaving((prev) => ({ ...prev, [milestoneKey]: true }));

		try {
			// Prepare the value - send null if empty
			const valueToSend =
				vdatValues[milestoneKey] && vdatValues[milestoneKey].trim() !== ""
					? vdatValues[milestoneKey]
					: null;

			const response = await apiClient.put(
				"/api/update-series-milestone-vdat",
				{
					snr: rowData.snr || rowData.SNR,
					snr_anlage_projnr: rowData.snr_anlage_projnr || rowData.project,
					milestone: milestoneKey,
					value: valueToSend,
				},
			);

			if (response.data.success) {
				// Update the row data if callback provided
				if (onMilestoneUpdate) {
					onMilestoneUpdate(milestoneKey, valueToSend || "");
				}

				setEditingVDAT((prev) => ({ ...prev, [milestoneKey]: false }));

				// Show success message
				if (valueToSend) {
					console.log(
						`✅ ${milestoneKey.toUpperCase()} VDAT updated to ${valueToSend}`,
					);
				} else {
					console.log(
						`✅ ${milestoneKey.toUpperCase()} VDAT cleared successfully`,
					);
				}
			}
		} catch (error) {
			console.error(`❌ Error updating ${milestoneKey} VDAT:`, error);
			// Reset value on error
			const detailData = rowData.milestones_detail?.[milestoneKey];
			setVdatValues((prev) => ({
				...prev,
				[milestoneKey]: detailData?.vdat || "",
			}));

			// Show error message
			alert(
				`Error updating ${milestoneKey.toUpperCase()} VDAT: ${error.response?.data?.detail || error.message}`,
			);
		} finally {
			setSaving((prev) => ({ ...prev, [milestoneKey]: false }));
		}
	};
	// Clear VDAT value - directly saves to backend
	const handleClearVDAT = async (milestoneKey) => {
		setClearing((prev) => ({ ...prev, [milestoneKey]: true }));

		try {
			const response = await apiClient.put(
				"/api/update-series-milestone-vdat",
				{
					snr: rowData.snr || rowData.SNR,
					snr_anlage_projnr: rowData.snr_anlage_projnr || rowData.project,
					milestone: milestoneKey,
					value: null, // Clear the date by sending null
				},
			);

			if (response.data.success) {
				// Clear the local state
				setVdatValues((prev) => ({
					...prev,
					[milestoneKey]: "",
				}));

				// Update the row data if callback provided
				if (onMilestoneUpdate) {
					onMilestoneUpdate(milestoneKey, "");
				}

				// Exit edit mode
				setEditingVDAT((prev) => ({ ...prev, [milestoneKey]: false }));

				console.log(
					`✅ ${milestoneKey.toUpperCase()} VDAT cleared successfully`,
				);
			}
		} catch (error) {
			console.error(`❌ Error clearing ${milestoneKey} VDAT:`, error);
			// Show error message
			alert(
				`Error clearing ${milestoneKey.toUpperCase()} VDAT: ${error.response?.data?.detail || error.message}`,
			);
		} finally {
			setClearing((prev) => ({ ...prev, [milestoneKey]: false }));
		}
	};

	// Cancel VDAT editing
	const handleCancelVDAT = (milestoneKey) => {
		const detailData = rowData.milestones_detail?.[milestoneKey];
		setVdatValues((prev) => ({
			...prev,
			[milestoneKey]: detailData?.vdat || "",
		}));
		setEditingVDAT((prev) => ({ ...prev, [milestoneKey]: false }));
	};

	// Handle VDAT date change
	const handleVDATChange = (milestoneKey, newDate) => {
		setVdatValues((prev) => ({
			...prev,
			[milestoneKey]: formatDate(newDate),
		}));
	}; // Determine if this is the P103988 project (uses LR99/MR99 instead of QI100/QI500/QI900)
	const isP103988 = rowData.project === "P103988";

	// Define milestone configurations — QI100/QI500/QI900 are replaced by LR99/MR99 for P103988
	const milestones = [
		{
			key: "fet",
			name: "FET",
			fullName: "Freeze Evolution Technology",
			hasDetails: true,
			vdatEditable: false,
		},
		{
			key: "a80",
			name: "A80",
			fullName: "Change Management (A80)",
			hasDetails: true,
			vdatEditable: true,
		},
		{
			key: "e99",
			name: "E99",
			fullName: "Procurement (E99)",
			hasDetails: true,
			vdatEditable: true,
		},
		...(!isP103988
			? [
					{
						key: "qi100",
						name: "QI100",
						fullName: "Quality Index 100",
						hasDetails: true,
						vdatEditable: true,
					},
					{
						key: "qi500",
						name: "QI500",
						fullName: "Quality Index 500",
						hasDetails: true,
						vdatEditable: false,
					},
					{
						key: "qi900",
						name: "QI900",
						fullName: "Quality Index 900",
						hasDetails: true,
						vdatEditable: false,
					},
				]
			: [
					{
						key: "lr99",
						name: "LR99",
						fullName: "Logistics Release 99 (LR99)",
						hasDetails: false, // no milestones_detail entry — bdat comes from milestones.lr99
						vdatEditable: false,
						bdatOnly: true, // custom flag: only BDAT is available
					},
					{
						key: "mr99",
						name: "MR99",
						fullName: "Manufacturing Release 99 (MR99)",
						hasDetails: false,
						vdatEditable: false,
						bdatOnly: true,
					},
				]),
		{
			key: "p99",
			name: "P99",
			fullName: "Logistics (P99)",
			hasDetails: true,
			vdatEditable: false,
		},
		{
			key: "pet",
			name: "PET",
			fullName: "Project End Technology",
			hasDetails: true,
			vdatEditable: false,
		},
	];

	const styles = {
		overlay: {
			position: "fixed",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			zIndex: 1000,
		},
		modal: {
			backgroundColor: "white",
			borderRadius: "8px",
			padding: "24px",
			maxWidth: "800px",
			width: "90%",
			maxHeight: "90vh",
			overflow: "auto",
			position: "relative",
			boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
		},
		header: {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: "20px",
			borderBottom: "1px solid #e5e7eb",
			paddingBottom: "16px",
		},
		title: {
			fontSize: "20px",
			fontWeight: "bold",
			color: "#111827",
			fontFamily: "MAN EUROPE_Bold",
		},
		closeButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			borderRadius: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		projectInfo: {
			marginBottom: "24px",
			padding: "16px",
			backgroundColor: "#f8f9fa",
			borderRadius: "6px",
		},
		projectTitle: {
			fontSize: "16px",
			fontWeight: "bold",
			marginBottom: "8px",
			fontFamily: "MAN EUROPE_Bold",
		},
		projectDetails: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
			gap: "8px",
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular",
		},
		th: {
			backgroundColor: "#f8f9fa",
			padding: "12px 8px",
			textAlign: "left",
			fontWeight: "bold",
			borderBottom: "2px solid #e5e7eb",
			fontFamily: "MAN EUROPE_Bold",
		},
		td: {
			padding: "12px 8px",
			borderBottom: "1px solid #e5e7eb",
			verticalAlign: "top",
		},
		milestoneCell: {
			fontWeight: "bold",
			color: "#374151",
		},
		dateCell: {
			fontFamily: "monospace",
			fontSize: "13px",
		},
		emptyDate: {
			color: "#9ca3af",
			fontStyle: "italic",
		},
		legend: {
			marginTop: "20px",
			padding: "16px",
			backgroundColor: "#ddd",
			borderRadius: "6px",
			borderLeft: "4px solid #1e2832",
		},
		legendTitle: {
			fontSize: "14px",
			fontWeight: "bold",
			marginBottom: "8px",
			fontFamily: "MAN EUROPE_Bold",
		},
		legendItems: {
			display: "flex",
			gap: "20px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular",
		},
		legendItem: {
			display: "flex",
			alignItems: "center",
			gap: "4px",
		},
	};
	return (
		<div style={styles.overlay} onClick={onClose}>
			<div
				style={styles.modal}
				onClick={(e) => e.stopPropagation()}
				data-tour-target="serie-quality-modal-content"
			>
				<div style={styles.header}>
					<h2 style={styles.title}>Milestone Details</h2>
					<button
						style={styles.closeButton}
						onClick={onClose}
						data-tour-target="serie-quality-modal-close"
					>
						<X size={24} color="#6b7280" />
					</button>
				</div>

				{/* Project Information */}
				<div style={styles.projectInfo}>
					<div style={styles.projectTitle}>Project Information</div>
					<div style={styles.projectDetails}>
						<div>
							<strong>ALG:</strong> {rowData.alg || "-"}
						</div>
						<div>
							<strong>SNR:</strong> {rowData.SNR || "-"}
						</div>
						<div>
							<strong>Description:</strong> {rowData.description || "-"}
						</div>
						<div>
							<strong>Project:</strong> {rowData.project || "-"}
						</div>
						<div>
							<strong>Change No:</strong> {rowData.changeNo || "-"}
						</div>
						<div>
							<strong>Supplier:</strong> {rowData.supplier || "-"}
						</div>
					</div>
				</div>

				{/* Milestone Table */}
				<table style={styles.table}>
					<thead>
						<tr>
							<th style={styles.th}>Milestone</th>
							<th style={styles.th}>Description</th>
							<th style={styles.th}>SDAT (Schedule Date)</th>
							<th style={styles.th}>VDAT (Verified Date)</th>
							<th style={styles.th}>BDAT (Baseline Date)</th>
						</tr>
					</thead>
					<tbody>
						{milestones.map((milestone) => {
							const detailData = rowData.milestones_detail?.[milestone.key];

							// LR99 / MR99: only BDAT is available, sourced from rowData.milestones
							if (milestone.bdatOnly) {
								const bdatValue = rowData.milestones?.[milestone.key] || null;
								return (
									<tr key={milestone.key}>
										<td style={{ ...styles.td, ...styles.milestoneCell }}>
											{milestone.name}
										</td>
										<td style={styles.td}>{milestone.fullName}</td>
										<td style={{ ...styles.td, ...styles.dateCell }}>
											<span style={styles.emptyDate}>-</span>
										</td>
										<td style={{ ...styles.td, ...styles.dateCell }}>
											<span style={styles.emptyDate}>-</span>
										</td>
										<td style={{ ...styles.td, ...styles.dateCell }}>
											{bdatValue ? (
												<span>{bdatValue}</span>
											) : (
												<span style={styles.emptyDate}>-</span>
											)}
										</td>
									</tr>
								);
							}

							return (
								<tr key={milestone.key}>
									<td style={{ ...styles.td, ...styles.milestoneCell }}>
										{milestone.name}
									</td>
									<td style={styles.td}>{milestone.fullName}</td>
									<td style={{ ...styles.td, ...styles.dateCell }}>
										{milestone.hasDetails ? (
											<span
												className={detailData?.sdat ? "" : "empty"}
												style={detailData?.sdat ? {} : styles.emptyDate}
											>
												{detailData?.sdat || "-"}
											</span>
										) : (
											<span style={styles.emptyDate}>N/A</span>
										)}{" "}
									</td>
									<td style={{ ...styles.td, ...styles.dateCell }}>
										{milestone.hasDetails ? (
											// Check if VDAT is editable for this milestone
											!milestone.vdatEditable ? (
												// Non-editable VDAT (qi500, qi900, p99)
												<span
													className={detailData?.vdat ? "" : "empty"}
													style={detailData?.vdat ? {} : styles.emptyDate}
												>
													{detailData?.vdat || "-"}
												</span>
											) : (
												// Editable VDAT
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}
												>
													{editingVDAT[milestone.key] ? (
														<LocalizationProvider dateAdapter={AdapterDateFns}>
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	gap: "4px",
																}}
															>
																<DatePicker
																	value={parseDate(vdatValues[milestone.key])}
																	onChange={(newDate) =>
																		handleVDATChange(milestone.key, newDate)
																	}
																	format="dd/MM/yyyy"
																	slotProps={{
																		textField: {
																			size: "small",
																			sx: {
																				"& .MuiInputBase-root": {
																					fontSize: "12px",
																					fontFamily: "monospace",
																				},
																			},
																		},
																	}}
																/>{" "}
																<Tooltip
																	title={
																		saving[milestone.key] ? "Saving..." : "Save"
																	}
																>
																	<IconButton
																		size="small"
																		onClick={() =>
																			handleSaveVDAT(milestone.key)
																		}
																		disabled={
																			saving[milestone.key] ||
																			clearing[milestone.key]
																		}
																		sx={{
																			color: "#22c55e",
																			"&:hover": {
																				backgroundColor:
																					"rgba(34, 197, 94, 0.1)",
																			},
																		}}
																	>
																		{saving[milestone.key] ? (
																			<CircularProgress
																				size={14}
																				sx={{ color: "#cc0000" }}
																			/>
																		) : (
																			<Save size={14} />
																		)}
																	</IconButton>{" "}
																</Tooltip>
																<Tooltip
																	title={
																		clearing[milestone.key]
																			? "Clearing..."
																			: "Clear Date"
																	}
																>
																	<IconButton
																		size="small"
																		onClick={() =>
																			handleClearVDAT(milestone.key)
																		}
																		disabled={
																			clearing[milestone.key] ||
																			saving[milestone.key]
																		}
																		sx={{
																			color: "#f59e0b",
																			"&:hover": {
																				backgroundColor:
																					"rgba(245, 158, 11, 0.1)",
																			},
																		}}
																	>
																		{clearing[milestone.key] ? (
																			<CircularProgress
																				size={14}
																				sx={{ color: "#f59e0b" }}
																			/>
																		) : (
																			<Trash2 size={14} />
																		)}
																	</IconButton>
																</Tooltip>{" "}
																<Tooltip title="Cancel">
																	<IconButton
																		size="small"
																		onClick={() =>
																			handleCancelVDAT(milestone.key)
																		}
																		disabled={
																			saving[milestone.key] ||
																			clearing[milestone.key]
																		}
																		sx={{
																			color: "#ef4444",
																			"&:hover": {
																				backgroundColor:
																					"rgba(239, 68, 68, 0.1)",
																			},
																		}}
																	>
																		<XIcon size={14} />
																	</IconButton>
																</Tooltip>
															</div>
														</LocalizationProvider>
													) : (
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "8px",
																width: "100%",
															}}
														>
															{" "}
															<span
																className={detailData?.vdat ? "" : "empty"}
																style={{
																	...(detailData?.vdat ? {} : styles.emptyDate),
																	flex: 1,
																	minHeight: "20px",
																	cursor: "pointer",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	border: "2px dashed #ddd",
																	display: "inline-block",
																	minWidth: "80px",
																	"&:hover": { backgroundColor: "#f5f5f5" },
																}}
																onClick={() =>
																	setEditingVDAT((prev) => ({
																		...prev,
																		[milestone.key]: true,
																	}))
																}
															>
																{detailData?.vdat || "-"}
															</span>
															<Tooltip title="Edit VDAT">
																<IconButton
																	size="small"
																	onClick={() =>
																		setEditingVDAT((prev) => ({
																			...prev,
																			[milestone.key]: true,
																		}))
																	}
																	sx={{
																		color: "#6b7280",
																		"&:hover": {
																			backgroundColor:
																				"rgba(107, 114, 128, 0.1)",
																			color: "#374151",
																		},
																	}}
																>
																	<Calendar size={12} />
																</IconButton>
															</Tooltip>
														</div>
													)}
												</div>
											)
										) : (
											<span style={styles.emptyDate}>N/A</span>
										)}
									</td>
									<td style={{ ...styles.td, ...styles.dateCell }}>
										{milestone.hasDetails ? (
											<span
												className={detailData?.bdat ? "" : "empty"}
												style={detailData?.bdat ? {} : styles.emptyDate}
											>
												{detailData?.bdat || "-"}
											</span>
										) : (
											<span
												className={detailData?.value ? "" : "empty"}
												style={detailData?.value ? {} : styles.emptyDate}
											>
												{detailData?.value || "-"}
											</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>

				{/* Legend */}
				<div style={styles.legend}>
					<div style={styles.legendTitle}>Date Type Legend:</div>
					<div style={styles.legendItems}>
						<div style={styles.legendItem}>
							<strong>SDAT:</strong> Schedule Date - Planned target date
						</div>
						<div style={styles.legendItem}>
							<strong>VDAT:</strong> Verified Date - Confirmed/verified date
						</div>
						<div style={styles.legendItem}>
							<strong>BDAT:</strong> Baseline Date - Initial baseline date
						</div>
					</div>{" "}
				</div>
			</div>
		</div>
	);
};

export default MilestoneDetailModal;
